import {createFileRoute, Navigate} from "@tanstack/react-router";
import {useTranslation} from "react-i18next";
import {Box, Divider} from "@mui/joy";
import {useSelf} from "../../../agent/useSelf.ts";
import {useMountedTruck} from "../../../driver/mountedTruck.ts";
import {useMothership, useOffloadableBundleIds} from "../../../tq/tqx.tsx";
import {TileSpread} from "../../../components/Tile.tsx";
import {DriverBundleTile} from "../../../driver/DriverBundleTile.tsx";
import {useScheduledTransfersForDriver} from "../../../driver/schedule.ts";
import {SuspenseDialog} from "../../../components/SuspenseDialog.tsx";
import {useQueries} from "@tanstack/react-query";
import {watchBundleByIdTqo, watchLastTriplegForTruckTqo} from "../../../rpc/webrpc-client.gen.ts";
import {useMemo} from "react";
import {useRefreshableInfallibleQuery} from "../../../util/tqrefresh.tsx";
import {cleanse, NADA} from "../../../util/arutil.tsx";
import {mergeUseQueriesWithInfill} from "../../../tq/mergeUseQueriesWithInfill.tsx";
import {PageBody, PageHeader, PageShell} from "../../../routing/shell.tsx";
import {TopBar} from "../../../components/TopBar.tsx";

// const cellStyle = {
//     color: "primary",
//     sx: {
//         margin: "2rem",
//         px: "4rem", py: "1.2rem"
//     }
// } as const;
//
// interface StartOnloadingButtonProps extends Omit<ButtonProps, "children"> {
//     bundleId: string;
// }

// function StartOnloadingTile(
//     {bundleId, color: inColor, variant: inVariant, ...rest}: StartOnloadingButtonProps) {
//     const {data: bundleData, isSuccess: isBundleSuccess}
//         = useQuery(activeBundleByIdTqo({id: bundleId}));
//     const {data: bundleStatus, isSuccess: isBundleStatusSuccess}
//         = useBundleStatus({bundleId, enabled: isBundleSuccess});
//     const color = inColor ?? "primary";
//     const variant = inVariant ?? "soft";
//     const bundle = bundleData?.bundle;
//     if (!isBundleSuccess || !bundle || !isBundleStatusSuccess) {
//         return null;
//     }
//     const {nextStepName, takeNextStep} = bundleStatus;
//
//     return <Sheet color={color} variant={variant} sx={TILE_SX}>
//         <Stack direction="column" spacing={1.6} sx={{alignItems: "center"}}>
//             <Box sx={{alignItems: "left", overflowX: "clip", maxWidth: "100%"}}>
//             <BundleCapsule bundle={bundle} color="primary" variant="soft" linkify={true}
//                            includeDetails={["dropoffVenue"]}/>
//             </Box>
//             {takeNextStep
//                 ? <Button {...cellStyle} {...rest} disabled={!takeNextStep}
//                           onClick={() => {
//                               void takeNextStep();
//                           }}>{nextStepName}
//                 </Button>
//                 : null}
//         </Stack>
//     </Sheet>
// }

export function VenueServicePage() {
    const {t} = useTranslation();
    const {id} = Route.useParams();
    const selfTqrv = useSelf();
    const self = selfTqrv.data;
    const mama = useMothership();
    const mountedTruckTqrv = useMountedTruck();
    const mountedTruck = mountedTruckTqrv.data;
    const lastTriplegTqrv = useRefreshableInfallibleQuery(watchLastTriplegForTruckTqo(mama,
        mountedTruck ? {truckId: mountedTruck.id} : undefined));
    const tripleg = lastTriplegTqrv.data;
    const myScheduledTransfersTqrv
        = useScheduledTransfersForDriver(self ? {driverId: self.id} : undefined);
    const myScheduledTransfers = myScheduledTransfersTqrv.data;
    const awaitingBundleIds = (myScheduledTransfers ?? NADA)
        .filter(maybe => maybe.venue.id === tripleg?.destination.id)
        .map(sxfer => sxfer.bundle.id);
    const possibleOffloadsTqrv
        = useOffloadableBundleIds(mountedTruck ? {truckId: mountedTruck.id, venueId: id} : undefined);
    const possibleOffloadIds = possibleOffloadsTqrv.data ?? NADA;
    const refStableTqos = useMemo(() => {
        return {
            items: [...possibleOffloadIds, ...awaitingBundleIds].map(id =>
                watchBundleByIdTqo(mama, {bundleId: id}))
        };
    }, [awaitingBundleIds, mama, possibleOffloadIds]);
    const serviceableBundleTqrvs = useQueries(
        myScheduledTransfersTqrv.data && possibleOffloadsTqrv.data
            ? {
                queries: refStableTqos.items,
                combine: mergeUseQueriesWithInfill,
            } : {queries: NADA});
    const serviceableBundles = cleanse(serviceableBundleTqrvs.data);

    if (!selfTqrv.isSuccess || !mountedTruckTqrv.isSuccess
        || !myScheduledTransfersTqrv.isSuccess || !lastTriplegTqrv.isSuccess
        || !possibleOffloadsTqrv.isSuccess || !serviceableBundleTqrvs.isSuccess) {
        return <SuspenseDialog message="venue service"/>;
    }

    if (!tripleg || !mountedTruck) {
        return <Navigate to="/driver/schedule" params={{}} replace={true}/>
    }

    if (!tripleg.admitted) {
        return <Navigate to="/truck/$id/drive" params={{id: mountedTruck.id}} replace={true}/>
    }

    return <PageShell>
        <PageHeader>
            <TopBar/>

            <Divider sx={{
                color: "primary.softColor", bgcolor: "primary.softBg",
                justifyContent: "center", width: "100%"
            }}>
                <Box sx={{fontSize: "lg"}}>{t("driver.venueServiceTasks")}</Box>
            </Divider>
        </PageHeader>
        <PageBody>
            <TileSpread foods={serviceableBundles} estimatedHeight={180}
                        Component={({food: bundle}) =>
                            <DriverBundleTile color="warning" variant="outlined" bundleStub={bundle}/>}/>
        </PageBody>
    </PageShell>;
}

export const Route = createFileRoute("/_auth/venue/$id/service")(
    {
        component: VenueServicePage,

        // THIS CAUSES THE ROUTER TO BECOME CIRCULAR SOMEHOW

        // beforeLoad:
        //     () => {
        //         return GLOBAL_MOTHERSHIP.self()
        //             .then(myself => GLOBAL_MOTHERSHIP.truckByActiveCrew({crewId: myself.self.id}))
        //             .then(truck => truck.truck
        //                 ? GLOBAL_MOTHERSHIP.watchLastTriplegForTruck({truckId: truck.truck.id})
        //                 : null)
        //             .then(tqrv => {
        //                 if (!tqrv?.triplegs || isEmpty(tqrv.triplegs)) {
        //                     return {};
        //                 } else {
        //                     return {
        //                         naviTriggerIcon: iconForEntityType("venue"),
        //                         naviTriggerLabel: tqrv.triplegs[0].destination.displayName,
        //                     };
        //                 }
        //             });
        //     }
    });

